import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { BackButton, Header, PaymentsTable, PaymentSummary } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { generateSchoolSessions } from '../utils/schoolYearUtils';
import { toast } from 'react-toastify';

const TransactionPerClass = ({ history }) => {
  const schoolSessions = generateSchoolSessions(2022, 20);
  const { school } = useStateContext();
  const [transactions, setTransactions] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState();
  const [term, setTerm] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const schoolId = school?._id;
  const currentTerm = school?.settings?.current_term;
  const currentSession = school?.settings?.current_session;
  const location = useLocation();
  const classId = location.pathname.split('/')[2];
  const nameOfClass = decodeURIComponent(location.pathname.split('/')[3] || 'Class');

  // Share the same persisted term/session as the rest of the Payments
  // section (see ClassPayments), so drilling into a class's transactions
  // keeps whatever term/session was already selected there.
  useEffect(() => {
    if (!classId || !schoolId) return;
    const t = localStorage.getItem('classPayments_term') || String(currentTerm || '');
    const s = localStorage.getItem('classPayments_session') || currentSession || '';
    setTerm(t);
    setSession(s);
    fetchData(t, s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, schoolId, currentTerm, currentSession]);

  const fetchData = async (t, s) => {
    setLoading(true);
    try {
      const [paymentsRes, summaryRes] = await Promise.all([
        userRequest.get(`/payments/school/${schoolId}?class_id=${classId}&term=${t}&session=${s}&limit=200&sort_by=date&order=des`),
        userRequest.get(`schools/${schoolId}/payment-summary?class_id=${classId}&term=${t}&session=${s}`),
      ]);
      setTransactions(paymentsRes.data.payments);
      setPaymentSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (paymentId) => {
    try {
      await userRequest.delete(`/payments/${paymentId}`);
      toast.success('Invoice deleted successfully.');
      fetchData(term, session);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete invoice.');
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    fetchData(term, session);
  };

  const resetToCurrentTerm = () => {
    const t = String(currentTerm || '');
    const s = currentSession || '';
    setTerm(t);
    setSession(s);
    localStorage.setItem('classPayments_term', t);
    localStorage.setItem('classPayments_session', s);
    fetchData(t, s);
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <BackButton history={history} />
      <Header category="Payments" title={nameOfClass} />

      {/* Inline filter bar */}
      <form
        onSubmit={submitHandler}
        className="flex flex-wrap items-end gap-3 mb-6 p-4 rounded-2xl border border-gray-200 bg-gray-50"
      >
        <div className="flex-1 min-w-[100px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Term</label>
          <select
            value={term || ''}
            onChange={(e) => {
              setTerm(e.target.value);
              localStorage.setItem('classPayments_term', e.target.value);
            }}
            className="w-full rounded-xl bg-white border border-gray-200 text-sm px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All</option>
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
            <option value="3">Term 3</option>
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Session</label>
          <select
            value={session || ''}
            onChange={(e) => {
              setSession(e.target.value);
              localStorage.setItem('classPayments_session', e.target.value);
            }}
            className="w-full rounded-xl bg-white border border-gray-200 text-sm px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All</option>
            {schoolSessions.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={resetToCurrentTerm}
          disabled={loading}
          className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 rounded-full disabled:opacity-50 transition-colors"
        >
          Reset to Current Term
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-full disabled:bg-indigo-300 transition-colors"
        >
          {loading ? 'Loading...' : 'Apply'}
        </button>
      </form>

      <PaymentSummary paymentSummary={paymentSummary} />
      <PaymentsTable transactions={transactions} loading={loading} nameOfClass={nameOfClass} onDeleteInvoice={handleDeleteInvoice} />
    </div>
  );
};

export default TransactionPerClass;
